import { useQuery } from '@tanstack/react-query';
import { Link, Redirect, useRoute } from 'wouter';
import { useAuth } from '@/contexts/auth-context';

interface ViewerChunk {
  /** null for mixed (room-composite) chunks; set on legacy per-participant chunks. */
  participantUserId: number | null;
  participantName: string | null;
  segmentIndex: number;
  chunkIndex: number;
  startedAt: string;
  endedAt: string;
  durationSec: number;
  costCents: number;
  whisperText: string;
  status: 'done' | 'failed' | 'pending' | 'empty';
  error?: string | null;
  audioUrl: string | null;
}

interface ViewerSegment {
  segmentIndex: number;
  projectId: string | null;
  startedAt: string | null;
  endedAt: string | null;
  chunks: ViewerChunk[];
}

interface ViewerSegmentSanity {
  segmentIndex: number;
  segmentDurationSec: number | null;
  maxParticipantChunkDurationSec: number;
  durationMismatchSec: number | null;
  chunkCount: number;
  doneChunks: number;
  failedChunks: number;
  pendingChunks: number;
  emptyChunks: number;
  zeroChunks: boolean;
  durationMismatchWarn: boolean;
}

interface ViewerSessionSanity {
  totalCostCents: number;
  totalChunks: number;
  doneChunks: number;
  failedChunks: number;
  pendingChunks: number;
  emptyChunks: number;
  segmentsWithZeroChunks: number[];
  segmentsWithDurationMismatch: number[];
  segments: ViewerSegmentSanity[];
}

interface ViewerSessionData {
  sessionId: string;
  startedAt: string;
  endedAt: string | null;
  status: string;
  projectId: string | null;
  segments: ViewerSegment[];
  sanity: ViewerSessionSanity;
}

interface ViewerScreenSnapshot {
  id: number;
  participantUserId: number;
  segmentIndex: number | null;
  capturedAt: string;
  status: string;
  mimeType: string | null;
  bytes: number | null;
  error: string | null;
  url: string | null;
}

export default function AdminTranscriptsDetailPage() {
  const { user, isLoading: authLoading } = useAuth();
  const [, params] = useRoute<{ sessionId: string }>('/admin/transcripts/:sessionId');
  const sessionId = params?.sessionId;

  const { data, isLoading, error } = useQuery<ViewerSessionData>({
    queryKey: ['/api/calls', sessionId, '_internal/transcripts'],
    enabled: !!user && user.role === 'admin' && !!sessionId,
  });

  // Phase D (#180): screen snapshots are a separate query so the audio
  // viewer keeps working even if the snapshot table is empty / fails.
  const { data: snapshotsData } = useQuery<{ snapshots: ViewerScreenSnapshot[] }>({
    queryKey: ['/api/calls', sessionId, '_internal/screen-snapshots'],
    enabled: !!user && user.role === 'admin' && !!sessionId,
  });
  const snapshots = snapshotsData?.snapshots ?? [];
  const snapshotsBySegment = new Map<string, ViewerScreenSnapshot[]>();
  for (const s of snapshots) {
    const k = s.segmentIndex === null ? 'unsegmented' : String(s.segmentIndex);
    if (!snapshotsBySegment.has(k)) snapshotsBySegment.set(k, []);
    snapshotsBySegment.get(k)!.push(s);
  }

  if (authLoading) {
    return <div className="p-6 font-mono text-sm text-gray-300">Loading…</div>;
  }
  if (!user || user.role !== 'admin') {
    return <Redirect to="/" />;
  }

  return (
    <div className="min-h-screen bg-black text-gray-200 p-6 font-mono text-sm">
      <div className="mb-4">
        <Link href="/admin/transcripts" className="text-blue-400 underline">
          ← back to list
        </Link>
      </div>
      <h1 className="text-lg font-bold mb-4" data-testid="transcripts-detail-title">
        Session {sessionId}
      </h1>
      {isLoading && <div>Loading…</div>}
      {error && (
        <div className="text-red-400" data-testid="transcripts-detail-error">
          Error: {(error as Error).message}
        </div>
      )}
      {data && (
        <>
          <div className="mb-4 text-gray-400">
            <div>status: {data.status}</div>
            <div>startedAt: {String(data.startedAt)}</div>
            <div>endedAt: {data.endedAt ? String(data.endedAt) : '—'}</div>
            <div>projectId: {data.projectId ?? '—'}</div>
            <div>segments: {data.segments.length}</div>
          </div>
          {/* Task 178 step 4: sanity assertions. */}
          <div
            className="mb-4 border border-yellow-700 bg-yellow-950/40 p-3"
            data-testid="sanity-summary"
          >
            <div className="font-bold mb-1">Sanity</div>
            <div data-testid="sanity-total-cost">
              total cost: {data.sanity.totalCostCents}¢
            </div>
            <div>
              chunks: <span data-testid="sanity-total-chunks">{data.sanity.totalChunks}</span>
              {' '}(done={data.sanity.doneChunks}{' '}
              <span data-testid="sanity-failed-chunks">failed={data.sanity.failedChunks}</span>{' '}
              pending={data.sanity.pendingChunks} empty={data.sanity.emptyChunks})
            </div>
            {data.sanity.segmentsWithZeroChunks.length > 0 && (
              <div className="text-red-400" data-testid="sanity-zero-chunk-segments">
                zero-chunk segments: {data.sanity.segmentsWithZeroChunks.join(', ')}
              </div>
            )}
            {data.sanity.segmentsWithDurationMismatch.length > 0 && (
              <div className="text-red-400" data-testid="sanity-mismatch-segments">
                duration-mismatch segments (&gt;5s gap): {data.sanity.segmentsWithDurationMismatch.join(', ')}
              </div>
            )}
            {data.sanity.segmentsWithZeroChunks.length === 0
              && data.sanity.segmentsWithDurationMismatch.length === 0
              && data.sanity.failedChunks === 0 && (
              <div className="text-green-400" data-testid="sanity-ok">all sanity checks pass</div>
            )}
          </div>
          {data.segments.length === 0 && (
            <div className="text-gray-500" data-testid="transcripts-detail-empty">
              No segments found.
            </div>
          )}
          {snapshotsBySegment.has('unsegmented') && (
            <div
              className="mb-6 border border-purple-700 bg-purple-950/20 p-3"
              data-testid="segment-unsegmented-snapshots"
            >
              <div className="font-bold mb-2">
                Screen snapshots (no open segment)
              </div>
              <SnapshotsStrip snapshots={snapshotsBySegment.get('unsegmented')!} segmentKey="unsegmented" />
            </div>
          )}
          {data.segments.map(seg => (
            <div
              key={seg.segmentIndex}
              className="mb-6 border border-gray-700 p-3"
              data-testid={`segment-${seg.segmentIndex}`}
            >
              <div className="font-bold mb-2">
                Segment #{seg.segmentIndex}{' '}
                <span className="text-gray-400 font-normal">
                  project={seg.projectId ?? '—'} startedAt={seg.startedAt ?? '—'} endedAt={seg.endedAt ?? '—'}
                </span>
              </div>
              {snapshotsBySegment.has(String(seg.segmentIndex)) && (
                <SnapshotsStrip
                  snapshots={snapshotsBySegment.get(String(seg.segmentIndex))!}
                  segmentKey={String(seg.segmentIndex)}
                />
              )}
              {seg.chunks.length === 0 ? (
                <div className="text-gray-500 italic">no chunks</div>
              ) : (
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-700 text-left">
                      <th className="py-1 pr-2">chunk</th>
                      <th className="py-1 pr-2">participant</th>
                      <th className="py-1 pr-2">status</th>
                      <th className="py-1 pr-2">dur(s)</th>
                      <th className="py-1 pr-2">¢</th>
                      <th className="py-1 pr-2">audio</th>
                      <th className="py-1 pr-2">text</th>
                    </tr>
                  </thead>
                  <tbody>
                    {seg.chunks.map(c => (
                      <tr
                        key={`${c.chunkIndex}-${c.participantUserId ?? 'call'}`}
                        className="border-b border-gray-800 align-top"
                        data-testid={`chunk-${seg.segmentIndex}-${c.chunkIndex}-${c.participantUserId ?? 'call'}`}
                      >
                        <td className="py-1 pr-2">{c.chunkIndex}</td>
                        <td className="py-1 pr-2">
                          {c.participantUserId == null ? 'Call (all participants)' : <>{c.participantName ?? `User ${c.participantUserId}`} (#{c.participantUserId})</>}
                        </td>
                        <td className="py-1 pr-2">
                          {c.status}
                          {c.error && (
                            <span className="text-red-400"> — {c.error}</span>
                          )}
                        </td>
                        <td className="py-1 pr-2">{c.durationSec}</td>
                        <td className="py-1 pr-2">{c.costCents}</td>
                        <td className="py-1 pr-2">
                          {c.audioUrl ? (
                            <audio
                              controls
                              preload="none"
                              src={c.audioUrl}
                              className="h-6"
                              data-testid={`audio-${seg.segmentIndex}-${c.chunkIndex}-${c.participantUserId ?? 'call'}`}
                            />
                          ) : (
                            <span className="text-gray-500">—</span>
                          )}
                        </td>
                        <td className="py-1 pr-2 whitespace-pre-wrap break-words max-w-2xl">
                          {c.whisperText || <span className="text-gray-500 italic">(empty)</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </>
      )}
    </div>
  );
}

function SnapshotsStrip({
  snapshots,
  segmentKey,
}: {
  snapshots: ViewerScreenSnapshot[];
  segmentKey: string;
}) {
  return (
    <div className="mb-3" data-testid={`snapshots-strip-${segmentKey}`}>
      <div className="text-gray-400 text-xs mb-1">
        screen snapshots ({snapshots.length})
      </div>
      <div className="flex flex-wrap gap-2">
        {snapshots.map(s => (
          <div
            key={s.id}
            className="border border-gray-700 p-1 bg-gray-900"
            data-testid={`snapshot-${s.id}`}
          >
            {s.status === 'warning_cap_reached' ? (
              <div className="w-32 h-20 flex items-center justify-center text-yellow-400 text-xs text-center px-1">
                cap reached — pipeline halted
              </div>
            ) : s.url ? (
              <a href={s.url} target="_blank" rel="noreferrer">
                <img
                  src={s.url}
                  alt={`snapshot ${s.id}`}
                  className="w-32 h-20 object-cover"
                  data-testid={`snapshot-img-${s.id}`}
                />
              </a>
            ) : (
              <div className="w-32 h-20 flex items-center justify-center text-red-400 text-xs text-center px-1">
                {s.status}
                {s.error ? <><br />{s.error}</> : null}
              </div>
            )}
            <div className="text-[10px] text-gray-500 mt-1">
              <div>user #{s.participantUserId}</div>
              <div>{new Date(s.capturedAt).toLocaleTimeString()}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
